<template>
    <!--
        min-h keeps the 320px design height for the collecting/closed states while letting the
        empty state grow. NewHomeCard's inner wrapper uses h-full, which does not resolve against
        a min-height, so the root is made a flex column and the wrapper told to grow from here.
    -->
    <NewHomeCard card-type="feedback_request" class="md:min-h-80 md:flex md:flex-col md:[&>div]:grow border! border-border!">
        <template #content>
            <FeedbackCreationModal
                v-model:open="showFeedbackDialog"
                :show-list-view="true"
                :feedback-questions="feedbackQuestions"
                :button-text="buttonText"
                :url-button-text="urlButtonText"
                :minimum-response-count="userFeedback.feedback_response_count_min"
                @delete="confirmDeleteIntentions"
                @copy-link="copyLink"
                @copy-url="copyUrl"
                @close-and-review="confirmConcludeAndReviewIntentions"
                @view-report="goToFeedbackReportFlow"
                @reopen="confirmReopenIntentions"
                @question-created="fetchFeedback" />
            <FeedbackConfirmationDialog
                v-model:open="showConfirmationDialog"
                :dialog-title="confirmationDialogTitle"
                :dialog-text="confirmationDialogText"
                :button-text="confirmationButtonText"
                @confirm="confirmationButtonCallback()" />

            <div class="flex flex-col gap-4 md:gap-6 items-start p-2 mt-auto">
                <!-- Expansion arrows to see more feedback will only show if there is feedback to show -->
                <div v-if="latestFeedback" class="absolute top-0 right-0 text-xl">
                    <BaseButton icon-only theme="neutral" class="rounded-full" aria-label="open feedback dialog" @click="seeFeedback()">
                        <i class="bi bi-arrows-angle-expand icon-bold text-base/none text-ink-muted" aria-hidden="true"></i>
                    </BaseButton>
                </div>
                <!-- Empty State -->
                <template v-if="!latestFeedback">
                    <FeedbackRequestIcon class="w-full max-w-51" />
                    <div class="flex flex-col gap-1 justify-center">
                        <h3 class="title-body text-ink">Gather feedback</h3>
                        <div class="body-small text-ink-muted">
                            Craft a question with Nadia and share an anonymous form with your peers. Then get personalized coaching on the feedback.
                        </div>
                    </div>
                    <BaseButton theme="neutral" @click="goToRequestFeedbackFlow()"> Start Collecting </BaseButton>
                </template>
                <!-- Latest Feedback -->
                <template v-else>
                    <div class="flex flex-col items-start grow gap-y-3 md:gap-y-5">
                        <div class="flex flex-col gap-2.5 justify-center grow">
                            <div class="scaffold-body-small text-ink-muted">
                                {{ responsesText }}
                            </div>
                            <div class="title-body bg-card-surface-raised p-2.5 rounded-lgr">
                                {{ latestFeedback.question }}
                            </div>
                        </div>
                        <div class="flex w-full gap-x-5 gap-y-2 justify-center sm:justify-start flex-wrap items-center">
                            <!-- Closed Feedback -->
                            <BaseButton v-if="responseMinimumMet && latestFeedback.report_cs_id" theme="neutral" @click="goToFeedbackReportFlow(latestFeedback)">
                                View Report
                            </BaseButton>
                            <!-- Open Feedback -->
                            <template v-else-if="!latestFeedback.report_cs_id">
                                <div class="flex justify-between w-full">
                                    <BaseToolTip side="bottom" :disabled="responseMinimumMet">
                                        <template #trigger>
                                            <BaseButton theme="neutral" :disabled="!responseMinimumMet" @click="confirmConcludeAndReviewIntentions(latestFeedback)">
                                                Close & Review
                                            </BaseButton>
                                        </template>
                                        <template #content>{{ toolTipText }}</template>
                                    </BaseToolTip>
                                    <BaseButton :disabled="buttonClicked" theme="tertiary" @click="copyLink(latestFeedback.id, 'blank_state')">
                                        {{ buttonText }}
                                    </BaseButton>
                                </div>
                            </template>
                        </div>
                    </div>
                </template>
            </div>
        </template>
    </NewHomeCard>
</template>

<script setup>
import BaseButton from "~vue/components/BaseButton.vue";
import BaseToolTip from "~vue/components/BaseTooltip.vue";
import { useFeedback } from "~vue/composables/useFeedback";
import FeedbackConfirmationDialog from "~vue/Feedback/FeedbackConfirmationDialog.vue";
import FeedbackCreationModal from "~vue/Feedback/FeedbackCreationModal.vue";
import FeedbackRequestIcon from "~vue/icons/FeedbackRequestIcon.vue";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { logUserInteraction } from "~vue/utils/logUtils";
import pluralize from "pluralize";
import { computed, inject, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";

import NewHomeCard from "./NewHomeCard.vue";

const { t } = useI18n();

const props = defineProps({
    userFeedback: { type: Object, required: true },
});

const toolTipText = computed(
    () =>
        `Once ${props.userFeedback.feedback_response_count_min} ${props.userFeedback.feedback_response_count_min === 1 ? "person responds" : "people respond"} I'll be able to generate your feedback`,
);
const latestFeedback = computed(() => {
    if (feedbackQuestionHistory.value) {
        return feedbackQuestionHistory.value.length > 0 ? feedbackQuestionHistory.value[0] : null;
    }
    return props.userFeedback.feedback_question_history?.length > 0 ? props.userFeedback.feedback_question_history[0] : null;
});

const responsesText = computed(() => {
    const count = latestFeedback.value?.response_count ?? 0;

    return `${count} ${pluralize("response", count)} collected on`;
});

const coachingSessionUuid = inject("coachingSessionUuid");

const buttonText = computed(() => (buttonClicked.value ? t("feedback.request.copied") : t("feedback.request.copyInvite")));
const urlButtonText = computed(() => (urlButtonClicked.value ? t("feedback.request.copied") : t("feedback.request.copyUrl")));

const showFeedbackDialog = ref(false);

const {
    confirmationDialogTitle,
    confirmationDialogText,
    confirmationButtonText,
    showConfirmationDialog,
    confirmationButtonCallback,
    goToFeedbackReportFlow,
    confirmDeleteIntentions,
    confirmConcludeAndReviewIntentions,
    confirmReopenIntentions,
    feedbackQuestionHistory,
    fetchFeedback,
    buttonClicked,
    copyLink,
    copyUrl,
    urlButtonClicked,
} = useFeedback();

const responseMinimumMet = computed(() => {
    return props.userFeedback.feedback_response_count_min <= latestFeedback.value.response_count;
});

const feedbackQuestions = computed(() => {
    return feedbackQuestionHistory.value || props.userFeedback.feedback_question_history;
});

function seeFeedback() {
    fetchFeedback();
    if (!showFeedbackDialog.value) {
        // Only log when opening the modal (not when closing)
        logUserInteraction(
            AmplitudeEventNames.FEEDBACK_MODAL_OPENED,
            {
                entry_point: "expand_button",
            },
            coachingSessionUuid,
        );
    }
    showFeedbackDialog.value = !showFeedbackDialog.value;
}

const goToRequestFeedbackFlow = () => {
    logUserInteraction(AmplitudeEventNames.FEEDBACK_GATHERING, {}, coachingSessionUuid);
    logUserInteraction(
        AmplitudeEventNames.FEEDBACK_MODAL_OPENED,
        {
            entry_point: "blank_state",
        },
        coachingSessionUuid,
    );
    showFeedbackDialog.value = true;
};

onMounted(() => {
    logUserInteraction(AmplitudeEventNames.FEEDBACK_HOMEPAGE_CARD_SHOWN, {}, coachingSessionUuid);

    // Auto-open feedback modal if URL param is present
    const params = new URLSearchParams(window.location.search);
    if (params.get("open_feedback") === "true") {
        fetchFeedback();
        showFeedbackDialog.value = true;
        logUserInteraction(AmplitudeEventNames.FEEDBACK_MODAL_OPENED, { entry_point: "url_param" }, coachingSessionUuid);
        // Clean up URL param
        params.delete("open_feedback");
        const newUrl = params.toString() ? `${window.location.pathname}?${params}` : window.location.pathname;
        window.history.replaceState({}, "", newUrl);
    }
});
</script>
